import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  TrendingUp,
  Users,
  MessageSquare,
  Target,
  Copy,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GoogleAds() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    webhook_key: '',
    auto_whatsapp: true,
    welcome_message: '¡Hola {nombre}! Gracias por tu interés a través de Google. ¿Te gustaría agendar una cita?'
  });

  const webhookUrl = `${API_URL}/api/webhook/google-ads/leads`;
  const webhookKey = 'aimedplanner_google_ads_2024';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [settingsRes, statsRes, leadsRes] = await Promise.all([
        fetch(`${API_URL}/api/clinic/${clinicId}/google-ads-settings`, { headers }),
        fetch(`${API_URL}/api/clinic/${clinicId}/google-ads-stats`, { headers }),
        fetch(`${API_URL}/api/clinic/${clinicId}/google-ads-leads`, { headers })
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(prev => ({ ...prev, ...data }));
      }

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      if (leadsRes.ok) {
        setLeads(await leadsRes.json());
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/clinic/${clinicId}/google-ads-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Configuración guardada correctamente' });
      } else {
        setMessage({ type: 'error', text: 'Error al guardar la configuración' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Search className="w-8 h-8 text-blue-500" />
              Google Ads
            </h1>
            <p className="text-gray-400 mt-1">
              Captura leads automáticamente desde tus campañas de Google Ads
            </p>
          </div>
          <a
            href="https://ads.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Ir a Google Ads
          </a>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Total Leads</p>
                    <p className="text-3xl font-bold text-white">{stats.total_leads}</p>
                  </div>
                  <Users className="w-10 h-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Esta Semana</p>
                    <p className="text-3xl font-bold text-white">{stats.this_week}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">WhatsApp Enviados</p>
                    <p className="text-3xl font-bold text-white">{stats.whatsapp_sent}</p>
                  </div>
                  <MessageSquare className="w-10 h-10 text-green-400 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Tasa Conversión</p>
                    <p className="text-3xl font-bold text-white">{stats.conversion_rate}%</p>
                  </div>
                  <Target className="w-10 h-10 text-cyan-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Configuration */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Configuración de Webhook</CardTitle>
            <CardDescription className="text-gray-400">
              Configura tu webhook en Google Ads para recibir leads automáticamente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable Switch */}
            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
              <div>
                <p className="text-white font-medium">Integración Activa</p>
                <p className="text-gray-400 text-sm">Recibe leads de Google Ads automáticamente</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            {/* Webhook URL */}
            <div className="space-y-2">
              <label className="text-white font-medium">URL del Webhook</label>
              <div className="flex gap-2">
                <Input
                  value={webhookUrl}
                  readOnly
                  className="bg-gray-900 border-gray-700 text-gray-300 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  className="border-gray-700"
                  onClick={() => copyToClipboard(webhookUrl)}
                >
                  {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-gray-500 text-sm">Copia esta URL en la configuración de Lead Forms de Google Ads</p>
            </div>

            {/* Webhook Key */}
            <div className="space-y-2">
              <label className="text-white font-medium">Clave del Webhook (Verify Token)</label>
              <div className="flex gap-2">
                <Input
                  value={webhookKey}
                  readOnly
                  className="bg-gray-900 border-gray-700 text-gray-300 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  className="border-gray-700"
                  onClick={() => copyToClipboard(webhookKey)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-gray-500 text-sm">Usa esta clave como "Webhook Key" en Google Ads</p>
            </div>

            {/* Auto WhatsApp */}
            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
              <div>
                <p className="text-white font-medium">Enviar WhatsApp Automático</p>
                <p className="text-gray-400 text-sm">Envía un mensaje de bienvenida a cada nuevo lead</p>
              </div>
              <Switch
                checked={settings.auto_whatsapp}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_whatsapp: checked }))}
              />
            </div>

            {/* Welcome Message */}
            {settings.auto_whatsapp && (
              <div className="space-y-2">
                <label className="text-white font-medium">Mensaje de Bienvenida</label>
                <Textarea
                  value={settings.welcome_message}
                  onChange={(e) => setSettings(prev => ({ ...prev, welcome_message: e.target.value }))}
                  className="bg-gray-900 border-gray-700 text-white min-h-[100px]"
                  placeholder="Usa {nombre} para personalizar el mensaje"
                />
                <p className="text-gray-500 text-sm">Variables disponibles: {'{nombre}'}</p>
              </div>
            )}

            {/* Message */}
            {message.text && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                {message.text}
              </div>
            )}

            {/* Save Button */}
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar Configuración
            </Button>
          </CardContent>
        </Card>

        {/* Setup Instructions */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Instrucciones de Configuración</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-gray-300">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
                <span>Ve a <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google Ads</a> y selecciona tu campaña con Lead Form Extensions</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
                <span>Ve a "Assets" → "Lead form extensions" → Edita tu formulario</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
                <span>En "Lead delivery options", selecciona "Webhook"</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
                <span>Pega la <strong>URL del Webhook</strong> y la <strong>Clave del Webhook</strong> de arriba</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
                <span>Haz clic en "Send test data" para verificar la conexión</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
                <span>Guarda los cambios y activa la integración aquí arriba</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Leads Recientes</CardTitle>
            <CardDescription className="text-gray-400">
              Últimos leads capturados desde Google Ads
            </CardDescription>
          </CardHeader>
          <CardContent>
            {leads.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay leads de Google Ads todavía</p>
                <p className="text-sm">Los leads aparecerán aquí cuando configures el webhook</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Nombre</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Teléfono</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Campaña</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">WhatsApp</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-3 px-4 text-white">{lead.name || 'N/A'}</td>
                        <td className="py-3 px-4 text-gray-300">{lead.email || 'N/A'}</td>
                        <td className="py-3 px-4 text-gray-300">{lead.phone || 'N/A'}</td>
                        <td className="py-3 px-4 text-gray-300">{lead.google_campaign_id || 'N/A'}</td>
                        <td className="py-3 px-4">
                          {lead.whatsapp_sent ? (
                            <span className="text-green-400 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> Enviado
                            </span>
                          ) : (
                            <span className="text-gray-500">Pendiente</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-400">
                          {new Date(lead.created_at).toLocaleDateString('es-MX')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
